/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Footer } from '@components/systems'
import { mono, monoForm, monoShared } from '@components/systems/monolith/monolith.tokens'

import { useNavigate } from 'react-router-dom'
import { useAppDispatch, paymentActions, CartType, CheckoutQuoteType } from '@redux/index'
import {
	Analytics,
	Decimal,
	CartQuote,
	CartKey,
	CartLineKey,
	formatAmount,
	PriceChangesType,
	checkoutAuthHeaders,
} from '@utils'
import { PriceChangeModal } from '@components/systems/monolith/payment/PriceChangeModal'
import { useDisplayCurrency } from '@components/systems/monolith/useDisplayCurrency'
import { paymentApi, ResType } from '@api'

// PLS 8 은 상점 목록(monolith/Shop.page)과 같은 컷 — 시즌상품 코드 PLS8 (PRODUCT_NAMES.md)
import pls8Image from '@public/images/monolith/koinos8-angle-full.png'
import kedem6Image from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Image from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import amp2Image from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb2.jpg'
import dsImage from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import noImage from '@public/images/pages/shop/noimage.jpg'

const imageObj: { [code: string]: string } = {
	PLS8: pls8Image,
	KDM6: kedem6Image,
	KDM4: kedem4Image,
	AMP2: amp2Image,
	DS: dsImage,
}

/**
 * Cart — Monolith(2안). 구 페이지는 `pages/home/Cart.page.tsx` 에 보관.
 *
 * **거래 로직은 옮기기만 했다** — localStorage `cart` 읽기·쓰기, 수량 상한(재고 - 판매분),
 * 금액 계산식(`Decimal`), `storage` 이벤트로 네비 배지 갱신, Checkout 의 두 dispatch 까지
 * 구 페이지 그대로다. 바뀐 것은 껍데기뿐이다.
 *
 * 외형은 DESIGN_SYSTEM §4·§5 — 라임 CTA·핑크 삭제 아이콘·radius 10 카드를 걷어내고
 * (§2 액센트 없음) Manuals 와 같은 헤어라인 제품 행 + 블랙 CTA 하나로 세웠다.
 * 수량은 구 페이지의 ± 버튼 대신 구매 패널(`MonolithBuyPanel`)과 같은 밑줄 숫자 입력이다 —
 * 같은 값을 두 화면에서 다른 문법으로 만지지 않는다.
 */
const MonolithCartPage = () => {
	// hook
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// state
	const [cartList, setCartList] = React.useState<CartType[]>([])
	/*
		서버 견적 (judahsWeb/CHECKOUT_PLAN.md §2). 줄 금액·합계는 이 값을 찍는다 — 장바구니에 담긴
		숫자는 "담을 때 본 값"이고, 지금 값은 서버가 안다. 다르면 장바구니를 고치고 무엇이 바뀌었는지
		문장으로 보여 준다. 배송지가 아직 없다 — 표시 통화가 원화면 KR 로, 아니면 USD 로 받는다
		(PRICING §6, 2026-09-16). 결제 통화는 결제 화면의 배송국이 다시 정한다.
	*/
	const [quote, setQuote] = React.useState<CheckoutQuoteType | null>(null)
	const currency = useDisplayCurrency()
	/** 견적 요청은 콜백 안에서 일어난다 — 최신 통화를 ref 로 읽는다 */
	const currencyRef = React.useRef(currency)
	currencyRef.current = currency
	const [notices, setNotices] = React.useState<string[]>(() => {
		try {
			const stored = sessionStorage.getItem('cartNotices')
			sessionStorage.removeItem('cartNotices')
			return stored ? (JSON.parse(stored) as string[]) : []
		} catch (error) {
			return []
		}
	})
	/** Checkout 을 누른 순간 값이 바뀌어 있으면 묻는다 (CHECKOUT_PLAN §13) */
	const [priceChanges, setPriceChanges] = React.useState<PriceChangesType | null>(null)

	const vatTotalUsd: number = React.useMemo(() => {
		const res = cartList.reduce(
			(sum, { sumUsd, vatUsd }) => Decimal(sum + sumUsd + vatUsd),
			0,
		)
		return res
	}, [cartList])

	React.useEffect(() => {
		getLocalCartList()
	}, [])

	const getLocalCartList = () => {
		const localeCartSrt = localStorage.getItem('cart')
		const res: CartType[] =
			localeCartSrt === null || localeCartSrt === '' ? [] : JSON.parse(localeCartSrt)

		setCartList(res)
		void fetchQuote(res, 0)
	}

	const fetchQuote = async (list: CartType[], depth: number) => {
		if (list.length === 0) {
			setQuote(null)
			return
		}

		const quoteCurrency = currencyRef.current
		const apiRes: ResType | undefined = await paymentApi.quote({
			body: {
				items: CartQuote.toQuoteItems(list, quoteCurrency),
				couponCode: '',
				// 서버는 KR 이면 KRW 로 견적한다(checkoutQuote.service). KR 배송비는 0 이라 합계가 어긋나지 않는다
				countryIsoCode: quoteCurrency === 'KRW' ? 'KR' : '',
				cartKey: CartKey.get(),
			},
			// 로그인 고객의 견적 — 판매 전 테스트 허용 고객(PREVIEW_BUYER_EMAILS)은 토큰이 있어야 풀린다
			customHeaders: checkoutAuthHeaders(),
		})

		if (apiRes === undefined || ![200, 201].includes(apiRes.status)) {
			return
		}

		const nextQuote = apiRes.data[0] as CheckoutQuoteType

		if (!nextQuote.ok) {
			// 값 변경은 여기서 고치지 않는다 — Checkout 을 누를 때 묻는다. 그 밖의 문제만 고친다
			const fix = CartQuote.applyProblems(list, nextQuote)

			if (fix.changed) {
				localStorage.setItem(
					'cart',
					fix.cartList.length === 0 ? '' : JSON.stringify(fix.cartList),
				)
				window.dispatchEvent(new Event('storage'))
				setCartList(fix.cartList)
				setNotices(
					fix.lineProblems.map(aProblem => CartQuote.describe(aProblem, quoteCurrency)),
				)

				if (depth < 2) {
					void fetchQuote(fix.cartList, depth + 1)
					return
				}
			}
		}

		setQuote(nextQuote)
	}

	/*
		화면의 통화. 견적이 있으면 견적의 통화, 없으면 표시 통화 — 단 담을 때 원화 단가를 못 받은 줄이
		하나라도 있으면(옛 장바구니) 견적 전에는 USD 로 그린다. 문법은 `formatAmount` — `.00` 없음.
	*/
	const localKrwOk: boolean = cartList.every(anItem => (anItem.unitKrw ?? 0) > 0)
	const showKrw: boolean =
		quote !== null ? quote.currency === 'KRW' : currency === 'KRW' && localKrwOk
	const money = (value: number): string => formatAmount(showKrw ? 'KRW' : 'USD', value)
	/** 단계 판매(얼리버드·프리오더) 줄이 섞여 있는가 — 취소 조건 한 줄이 그때만 선다 */
	const hasPreorderLine: boolean =
		quote !== null &&
		quote.lines.some(aLine => ['earlybird', 'preorder'].includes(aLine.phase))
	/** 견적 전 줄 금액 — 담을 때 본 값(옵션 추가금 포함) */
	const localLine = (anItem: CartType): number =>
		showKrw
			? Math.round(CartQuote.localUnitKrw(anItem) * anItem.qty)
			: Decimal(CartQuote.localUnitUsd(anItem) * anItem.qty)
	const localTotal: number = showKrw
		? cartList.reduce((sum, anItem) => sum + localLine(anItem), 0)
		: vatTotalUsd

	// 표시 통화가 바뀌면(서버 판정 도착 · 푸터 스위치) 그 통화로 견적을 다시 받는다
	React.useEffect(() => {
		if (cartList.length > 0) void fetchQuote(cartList, 0)
	}, [currency])

	/**
	 * 수량 변경. 상한은 구 페이지의 ± 버튼과 같은 값이다 — 시즌 수량에서 판매분을 뺀 만큼.
	 * 금액 세 줄(`sumUsd`·`vatUsd`)의 계산식도 구 페이지와 한 글자도 다르지 않다.
	 */
	/** 같은 제품의 다른 줄(다른 옵션)이 이미 쓰는 수량 — 재고 상한을 줄들이 나눠 쓴다 */
	const otherLinesQty = (key: string, seasonProductId: string): number =>
		cartList
			.filter(anItem => anItem.id === seasonProductId && CartLineKey.of(anItem) !== key)
			.reduce((sum, anItem) => sum + anItem.qty, 0)

	const onQtyChange = (key: string, value: string) => {
		const idx = cartList.findIndex(anItem => CartLineKey.is(anItem, key))
		if (idx === -1) return

		const room: number = Math.max(
			1,
			cartList[idx].seasonQuantity -
				cartList[idx].soldQuantity -
				otherLinesQty(key, cartList[idx].id),
		)
		const typedQty = Number(value)
		const nextQty =
			isNaN(typedQty) || typedQty < 1 ? 1 : typedQty > room ? room : typedQty

		const newCartList: CartType[] = cartList.map((anItem, i) =>
			i === idx
				? {
						...anItem,
						qty: nextQty,
						sumUsd: Decimal(CartQuote.localUnitUsd(anItem) * nextQty),
						vatUsd: 0,
				  }
				: anItem,
		)

		saveAndSync(newCartList)
	}

	const onItemDeleteClick = (key: string) => {
		const newCartList: CartType[] = cartList.filter(
			anItem => !CartLineKey.is(anItem, key),
		)

		saveAndSync(newCartList)
	}

	/** 저장 → 상시판매 카트 홀드 동기화(허락 수량으로 조정) → 견적 */
	const saveAndSync = (list: CartType[]) => {
		localStorage.setItem('cart', list.length === 0 ? '' : JSON.stringify(list))
		window.dispatchEvent(new Event('storage'))

		void CartQuote.syncHolds(list).then(({ cartList: synced, reduced }) => {
			localStorage.setItem('cart', synced.length === 0 ? '' : JSON.stringify(synced))
			window.dispatchEvent(new Event('storage'))
			if (reduced.length > 0) {
				setNotices(prev => [...prev, ...reduced])
			}
			getLocalCartList()
		})
	}

	const goToCheckout = (list: CartType[]) => {
		Analytics.beginCheckout(list)
		reduxDispatch(paymentActions.PAYMENT_METHOD(''))
		reduxDispatch(
			paymentActions.TRY_TO_PAYMENT({
				cartList: list,
			}),
		)
	}

	/*
		카트 → 결제로 넘어가는 순간 값이 바뀌어 있으면 묻는다 (사용자 결정 2026-09-16).
		내렸으면 알리고 넘어간다. 올랐으면 어떤 제품이 얼마에서 얼마로 올랐는지 줄마다 적고 체크박스로
		구매 의사를 받는다 — 체크된 것만 결제 화면으로 간다.
	*/
	const onSubmitClick = () => {
		if (quote === null) {
			goToCheckout(cartList)
			return
		}

		const changes = CartQuote.priceChanges(cartList, quote)

		if (changes.increased.length === 0 && changes.decreased.length === 0) {
			goToCheckout(cartList)
			return
		}

		setPriceChanges(changes)
	}

	const onPriceDecision = (keepIds: string[]) => {
		if (quote === null || priceChanges === null) return

		const nextCartList = CartQuote.applyPriceDecision(
			cartList,
			quote,
			priceChanges,
			keepIds,
		)
		setPriceChanges(null)
		localStorage.setItem(
			'cart',
			nextCartList.length === 0 ? '' : JSON.stringify(nextCartList),
		)
		window.dispatchEvent(new Event('storage'))
		setCartList(nextCartList)

		if (nextCartList.length === 0) {
			setQuote(null)
			return
		}

		goToCheckout(nextCartList)
	}

	return (
		<View css={style}>
			<View className="mnWrap">
				<View className="mnCartHeader">
					<h1 className="mnCartTitle">Cart</h1>
					<p className="mnCartLede">
						{cartList.length === 0
							? 'Nothing here yet.'
							: 'What you are about to order. The quantity field stops at what is left in stock.'}
					</p>
				</View>

				{notices.length > 0 && (
					<View className="mnCartNotices">
						{notices.map((aNotice, index) => (
							<p key={index} className="mnCartNotice">
								{aNotice}
							</p>
						))}
					</View>
				)}

				{cartList.length === 0 ? (
					<View className="mnCartEmpty">
						<p className="mnCartEmptyText">The cart is empty.</p>
						<button
							type="button"
							className="mnCtaGhost"
							onClick={() => navigate('/shop')}
						>
							Go to Shop
						</button>
					</View>
				) : (
					<>
						<View className="mnCartList">
							{cartList.map(anItem => {
								// 시즌상품 코드는 'pls8' 처럼 소문자로도 온다 — 대문자로 맞춰 찾는다
								const imageKey = (anItem.productCode ?? '').toUpperCase()
								const image = imageKey in imageObj ? imageObj[imageKey] : noImage
								const key = CartLineKey.of(anItem)
								const optionId = anItem.option?.id ?? ''
								const room: number = Math.max(
									1,
									anItem.seasonQuantity -
										anItem.soldQuantity -
										otherLinesQty(key, anItem.id),
								)

								return (
									<View key={key} className="mnCartRow">
										<button
											type="button"
											className="mnCartThumb"
											onClick={() =>
												navigate(`/products/${anItem.productCode.toLowerCase()}`)
											}
											aria-label={anItem.productName}
										>
											<img src={image} alt={anItem.productName} />
										</button>

										<View className="mnCartInfo">
											<View className="mnCartName">
												<p className="mnCartProduct">{anItem.productName}</p>
												{anItem.option?.id !== '' && (
													<span className="mnCartOption">{anItem.option?.name}</span>
												)}
												{(() => {
													const line = CartQuote.line(quote, anItem.id, optionId)
													if (line === undefined) return null
													const label =
														line.phase === 'earlybird'
															? 'Early bird'
															: line.phase === 'preorder'
															? 'Pre-order'
															: ''
													const reserved =
														line.alwaysAvailable && line.holdExpiresAt !== ''
															? `Reserved until ${new Date(
																	line.holdExpiresAt,
															  ).toLocaleString('en-US', {
																	month: 'short',
																	day: 'numeric',
																	hour: '2-digit',
																	minute: '2-digit',
															  })}`
															: line.alwaysAvailable
															? 'Not reserved'
															: 'Not reserved · first paid, first served'
													return (
														<>
															<span className="mnCartOption">
																{`${label !== '' ? `${label} · ` : ''}${money(
																	line.unitAmount + line.optionAmount,
																)} each`}
															</span>
															<span className="mnCartOption">{reserved}</span>
														</>
													)
												})()}
											</View>

											<View className="mnCartControl">
												<View className="mnCartQtyField">
													<label className="mnLabel" htmlFor={`mnCartQty-${key}`}>
														Qty
													</label>
													<input
														id={`mnCartQty-${key}`}
														className="mnInput mnCartQty"
														type="number"
														min={1}
														max={room}
														value={anItem.qty}
														onChange={e => onQtyChange(key, e.target.value)}
													/>
												</View>

												<span className="mnCartLineUsd">
													{(() => {
														const line = CartQuote.line(quote, anItem.id, optionId)
														return line === undefined
															? money(localLine(anItem))
															: money(line.lineAmount)
													})()}
												</span>

												<button
													type="button"
													className="mnCartRemove"
													onClick={() => onItemDeleteClick(key)}
												>
													Remove
												</button>
											</View>
										</View>
									</View>
								)
							})}
						</View>

						<View className="mnCartFoot">
							<View className="mnCartSum">
								<View className="mnCartTotal">
									<span className="mnLabel">Total</span>
									<span className="mnCartTotalValue">
										{quote === null ? money(localTotal) : money(quote.subtotalAmount)}
									</span>
								</View>

								<button type="button" className="mnCta mnCartCta" onClick={onSubmitClick}>
									Checkout
								</button>

								<p className="mnCartNote">
									Shipping and duties are settled in the next step.
								</p>

								{hasPreorderLine && (
									/*
										프리오더 줄이 섞여 있으면 취소 조건을 여기서도 말한다 —
										스토어 정책과 같은 문장이다(`storePolicy_en.md` Pre-Order Policy).
									*/
									<p className="mnCartNote">
										{
											'You can cancel a pre-order any time before it ships. We refund the full amount.'
										}
									</p>
								)}
							</View>
						</View>
					</>
				)}
			</View>

			<PriceChangeModal
				show={priceChanges !== null}
				changes={priceChanges ?? { currency: 'USD', increased: [], decreased: [] }}
				onConfirm={onPriceDecision}
				onCancel={() => setPriceChanges(null)}
			/>

			<Footer />
		</View>
	)
}

export default MonolithCartPage

const style = css({
	...monoShared,
	...monoForm,
	position: 'relative',
	width: '100%',

	'.mnCartHeader': {
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(32px, 3.6vw, 48px)',

		'.mnCartTitle': {
			fontSize: 'clamp(44px, 6vw, 88px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnCartLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnCartNotices': {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		padding: '0 0 24px',

		'.mnCartNotice': {
			fontSize: 14,
			fontWeight: 300,
			color: mono.ink,
		},
	},

	'.mnCartEmpty': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 32,
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(48px, 5.5vw, 80px) 0 clamp(64px, 8.3vw, 120px)',

		'.mnCartEmptyText': {
			fontSize: 16,
			fontWeight: 300,
			color: mono.dim,
		},
	},

	/** 제품 행 — Manuals 와 같은 4:8 헤어라인 행 (DESIGN_SYSTEM §12.1) */
	'.mnCartList': {
		display: 'flex',
		flexDirection: 'column',
	},

	'.mnCartRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 220px) minmax(0, 1fr)',
		gap: 'clamp(24px, 4vw, 56px)',
		alignItems: 'start',
		padding: 'clamp(32px, 3.6vw, 48px) 0',
		borderTop: `1px solid ${mono.line}`,

		'&:last-of-type': {
			borderBottom: `1px solid ${mono.line}`,
		},

		'@media (max-width: 700px)': {
			gridTemplateColumns: '1fr',
			gap: 24,
		},
	},

	'.mnCartThumb': {
		display: 'block',
		width: '100%',
		padding: 0,
		border: `1px solid ${mono.line}`,
		borderRadius: 6,
		background: mono.surface,
		overflow: 'hidden',
		cursor: 'pointer',

		img: {
			display: 'block',
			width: '100%',
			height: 'auto',
		},

		'@media (max-width: 700px)': {
			maxWidth: 260,
		},
	},

	'.mnCartInfo': {
		display: 'flex',
		flexDirection: 'column',
		gap: 'clamp(24px, 2.8vw, 40px)',
		height: '100%',
	},

	'.mnCartName': {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,

		'.mnCartProduct': {
			fontSize: 'clamp(24px, 2.2vw, 32px)',
			lineHeight: 1.1,
			fontWeight: 300,
			color: mono.ink,
		},
		'.mnCartOption': {
			fontSize: 14,
			fontWeight: 300,
			color: mono.faint,
		},
	},

	'.mnCartControl': {
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'space-between',
		gap: 32,
		flexWrap: 'wrap',
		marginTop: 'auto',

		'.mnCartQtyField': {
			display: 'flex',
			flexDirection: 'column',
			gap: 12,
			width: 88,

			'.mnCartQty': {
				height: 40,
			},
		},

		'.mnCartLineUsd': {
			fontSize: 22,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums',
		},

		'.mnCartRemove': {
			padding: 0,
			border: 0,
			background: 'transparent',
			fontSize: 13,
			fontWeight: 500,
			color: mono.dim,
			cursor: 'pointer',
			borderBottom: '1px solid transparent',
			paddingBottom: 2,
			transition: 'color 200ms ease-out',

			'&:hover': {
				color: mono.ink,
				borderBottomColor: mono.ink,
			},
		},
	},

	'.mnCartFoot': {
		display: 'flex',
		justifyContent: 'flex-end',
		padding: 'clamp(40px, 4.4vw, 64px) 0 clamp(64px, 8.3vw, 120px)',

		'.mnCartSum': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 32,
			width: '100%',
			maxWidth: 380,
		},

		'.mnCartTotal': {
			display: 'flex',
			alignItems: 'baseline',
			justifyContent: 'space-between',
			gap: 24,
			width: '100%',
			paddingBottom: 22,
			borderBottom: `1px solid ${mono.line}`,

			'.mnCartTotalValue': {
				fontSize: 'clamp(30px, 3vw, 40px)',
				lineHeight: 1,
				fontWeight: 200,
				color: mono.ink,
				fontVariantNumeric: 'tabular-nums',
			},
		},

		'.mnCartCta': {
			width: '100%',
		},

		'.mnCartNote': {
			fontSize: 15,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
		},

		'@media (max-width: 700px)': {
			justifyContent: 'flex-start',
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnCartRemove': { transition: 'none' },
	},
})
